import React, { useState, useEffect } from 'react';
import { Image } from '@phosphor-icons/react';
import api from '../services/api';
import { hasAbsoluteScore, getScoreLabel } from '../utils/itemHelpers';
import { MUSIC_SERVICES, useMusicService } from '../utils/musicService';
import AppleMusicIcon from './AppleMusicIcon';

/**
 * Compact inline card for referenced items in chat messages.
 * Shows thumbnail + title + category badge. Clickable to open detail modal.
 *
 * Renders purely from the structured item the server sent (COL-38) — both
 * live replies and reloaded history carry presigned thumbnail/image URLs, so
 * a grid of cards costs zero API calls. A card with no image simply shows the
 * placeholder; the full item is fetched once, on click, by the modal.
 */
export default function ChatItemCard({ item, onItemClick }) {
  const [imageUrl, setImageUrl] = useState(null);
  const itemId = item.item_id || item.id;
  const title = item.headline || item.summary || item.title || 'Untitled';
  const category = item.category;
  const placeData = item.place_data;
  const rating = placeData?.rating;
  const address = placeData?.short_formatted_address;
  const scoreLabel = getScoreLabel(item);
  // No Spotify link here (COL-261): Spotify's terms require the full logo +
  // wordmark, min ~70px wide, and this card's glyph slot is a 28px overlay in
  // a compact row — too narrow to render it legibly, and shrinking to an
  // icon-only mark is not permitted. Apple-only on this surface is accepted,
  // which also means a user whose service is Spotify (or who has none) gets
  // no music link on this card rather than one for a service they don't use.
  const musicService = useMusicService();
  const showAppleMusic =
    musicService === MUSIC_SERVICES.APPLE || musicService === MUSIC_SERVICES.BOTH;
  const appleMusicUrl = showAppleMusic
    ? item.entity_details?.find(e => e.apple_music_url)?.apple_music_url
    : undefined;

  // Resolve the thumbnail from data we already have — never by fetching the
  // item (that was the N+1 this contract removed).
  useEffect(() => {
    if (!itemId) return;

    // Presigned thumbnail/image URL from backend enrichment
    const enrichedUrl = item.thumbnail || item.image;
    if (enrichedUrl && enrichedUrl.startsWith('http')) {
      setImageUrl(enrichedUrl);
      return;
    }
    setImageUrl(null);

    // Filename path, for items handed in from Gallery/deep links rather than
    // from a chat response
    const filename = item.thumbnail_filename || item.filename;
    if (filename) {
      let cancelled = false;
      api.getImageUrl(filename)
        .then(({ url }) => { if (!cancelled) setImageUrl(url); })
        .catch(() => {});
      return () => { cancelled = true; };
    }
  }, [itemId, item.thumbnail, item.image, item.thumbnail_filename, item.filename]);

  const handleClick = () => {
    onItemClick?.({ item_id: itemId, ...item });
  };

  return (
    <div className={`chat-item-card-wrap${appleMusicUrl ? ' has-apple-music' : ''}`}>
      <button
        className="chat-item-card"
        onClick={handleClick}
        title={`View: ${title}`}
      >
        {imageUrl ? (
          <img
            className="chat-item-card-thumb"
            src={imageUrl}
            alt=""
            loading="lazy"
          />
        ) : (
          <div className="chat-item-card-thumb chat-item-card-placeholder">
            <ImageIcon />
          </div>
        )}
        <div className="chat-item-card-body">
          <span className="chat-item-card-title">{title}</span>
          {category && (
            <span className="chat-item-card-badge">
              {category}
              {(item.subcategories || [])[0] && (
                <span className="chat-item-tag-sub"> · {item.subcategories[0]}</span>
              )}
            </span>
          )}
          {(rating || address) && (
            <div className="chat-item-place">
              {rating && <span className="chat-item-rating">{'★'} {rating}</span>}
              {address && <span className="chat-item-address">{address}</span>}
            </div>
          )}
        </div>
        {hasAbsoluteScore(item) && (
          <div
            className="chat-item-score-bar"
            style={{ opacity: Math.max(0.25, Math.min(1, item.score)) }}
            {...(scoreLabel && { title: scoreLabel })}
          />
        )}
      </button>
      {appleMusicUrl && (
        <a
          href={appleMusicUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open in Apple Music"
          className="chat-item-card-apple-music"
          onClick={e => e.stopPropagation()}
        >
          <AppleMusicIcon />
        </a>
      )}
    </div>
  );
}

function ImageIcon() {
  return <Image size={16} />;
}
